import axios from "axios";
import {
	requestHeaderNormalizerInterceptor,
	authInterceptorFactory,
	extendUserProfile
} from "@teaming/io-helpers";

const IO_USER_NAMESPACE = "user";

export default function ioUserFactory( { config: { baseUri, }, authService, } ) {
	axios.defaults.baseURL = baseUri;
	axios.interceptors.request.use( requestHeaderNormalizerInterceptor );
	axios.interceptors.request.use( authInterceptorFactory( { authService, } ) );

	const cacheGordon = {}; // he'll save every one of us.

	async function _getPresignedUploadUrl( fileName ) {
		return axios.get( `/user/upload-url?fileName=${ fileName }` );
	}

	return {
		namespace: IO_USER_NAMESPACE,
		async activity( { activityType, metadata = {}, } ) {
			return axios( {
				method: "post",
				url: "/user/me/activity",
				data: {
					type: activityType,
					metadata,
				},
			} );
		},
		async fetchUser( { personId, } ) {
			// right now this is soley a one-off axios call, not persisting this in the cache yet intentionally
			const response = await axios.get( `/user/${ personId }` );
			return response.data;
		},
		async getUserProfile( { refetch, } = { refetch: false, } ) {
			if ( !cacheGordon.user || refetch ) {
				const [ userProfileResponse, userSettingsResponse, ] = await Promise.all( [
					axios.get( "/user/me" ),
					axios.get( "/user/me/settings" ),
				] );

				const userProfile = userProfileResponse.data;
				const settings = userSettingsResponse.data;
				userProfileResponse.data = extendUserProfile( { userProfile, settings, } );
				cacheGordon.user = userProfileResponse;
			}

			return Promise.resolve( cacheGordon.user );
		},
		recordOnboardingStep( { step, isComplete, isTeamLead, } ) {
			return axios(
				{
					method: "post",
					url: "/user/me/onboarding/step",
					headers: {
						"x-api-version": 3, // eslint-disable-line no-magic-numbers
					},
					data: { step, isComplete, isTeamLead, },
				}
			);
		},
		saveAssessment( { assessment, } ) {
			return axios(
				{
					method: "post",
					url: "/user/me/assessment",
					data: assessment,
				}
			);
		},
		async saveUserProfile( userProfile ) {
			const userProfileResponse = await axios(
				{
					method: "put",
					url: "/user/me",
					data: userProfile,
				}
			);

			const nextUserProfile = userProfileResponse.data;
			userProfileResponse.data = extendUserProfile( { userProfile: nextUserProfile, } );
			return userProfileResponse;
		},
		updateSettings( settings ) {
			// notificationDeliveryTypes and notificationTypes are read-only and cannot
			// be updated via the settings endpoint -- omit them here, just in case
			const {
				// eslint-disable-next-line no-unused-vars
				notificationDeliveryTypes,
				// eslint-disable-next-line no-unused-vars
				notificationTypes,
				...patchedSettings
			} = settings;
			return axios( {
				method: "post",
				url: "/user/me/settings",
				data: patchedSettings,
			} );
		},
		updateNotificationSettings( { notifications, } ) {
			return this.updateSettings( { notifications, } );
		},
		createCustomerBillingPortal() {
			return axios(
				{
					method: "post",
					url: "user/me/billing-portal",
					data: {
						returnUrl: window.location.href,
					},
				}
			);
		},
		requestEmailVerification() {
			return axios( {
				method: "post",
				url: "user/me/verification-email",
				data: {},
			} );
		},
		async uploadFile( { formFile, fileName, } ) {
			const response = await _getPresignedUploadUrl( fileName );
			const signedUrl = response.data;
			return fetch( signedUrl, {
				method: "PUT",
				body: formFile,
			} );
		},
	};
}
